import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, AlertTriangle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { deliveryLabel } from '@/lib/deliveryModes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MarkFailedCourier = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [memberId, setMemberId] = useState('');
  const [latestDispatch, setLatestDispatch] = useState(null);
  const [dispatchHistory, setDispatchHistory] = useState([]);
  const [failureReason, setFailureReason] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const handleSearch = async () => {
    if (!memberId.trim()) {
      toast.error('Please enter a Member ID');
      return;
    }

    setLoading(true);
    try {
      // Fetch dispatch history
      const historyResponse = await axios.get(`${API}/dispatches/member/${memberId}/history`);
      setDispatchHistory(historyResponse.data || []);
      
      // Fetch latest/failed dispatch
      const response = await axios.get(`${API}/dispatches/member/${memberId}/latest`);
      setLatestDispatch(response.data);
      setFailureReason('');
      
      if (response.data.delivery_status === 'failed') {
        toast.info('Found a failed dispatch for this member');
      } else {
        toast.success('Latest dispatch found');
      }
    } catch (error) {
      console.error('Error fetching dispatch:', error);
      if (error.response?.status === 404) {
        toast.error('No dispatch found for this Member ID');
      } else {
        toast.error('Failed to fetch dispatch details');
      }
      setLatestDispatch(null);
      setDispatchHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkFailed = async () => {
    if (!latestDispatch) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/dispatches/${latestDispatch.id}/mark-failed?failure_reason=${encodeURIComponent(failureReason)}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Dispatch marked as failed successfully');
      
      // Refresh the dispatch data
      const historyResponse = await axios.get(`${API}/dispatches/member/${memberId}/history`);
      setDispatchHistory(historyResponse.data || []);
      const response = await axios.get(`${API}/dispatches/member/${memberId}/latest`);
      setLatestDispatch(response.data);
      setFailureReason('');
    } catch (error) {
      console.error('Error marking dispatch as failed:', error);
      toast.error('Failed to mark dispatch as failed');
    } finally {
      setLoading(false);
    }
  };

  // Mark specific dispatch as failed from history
  const handleMarkSpecificFailed = async (dispatch, reason = '') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/dispatches/${dispatch.id}/mark-failed?failure_reason=${encodeURIComponent(reason)}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Dispatch marked as failed successfully');
      
      // Refresh data
      const historyResponse = await axios.get(`${API}/dispatches/member/${memberId}/history`);
      setDispatchHistory(historyResponse.data || []);
      const response = await axios.get(`${API}/dispatches/member/${memberId}/latest`);
      setLatestDispatch(response.data);
    } catch (error) {
      console.error('Error marking dispatch as failed:', error);
      toast.error('Failed to mark dispatch as failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setMemberId('');
    setLatestDispatch(null);
    setDispatchHistory([]);
    setFailureReason('');
    setShowHistory(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#7c3aed' }}>
              Mark Failed Courier
            </h1>
          </div>
        </div>

        {/* Search Section */}
        <div className="form-section mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#7c3aed' }}>
            Search Dispatch
          </h2>
          <div className="flex gap-4 items-end">
            <div className="flex-1 space-y-2">
              <Label>Member/Advertiser ID *</Label>
              <Input
                value={memberId}
                onChange={(e) => setMemberId(e.target.value.toUpperCase())}
                placeholder="Enter ID (e.g., ATMR1234, ATADV458)"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button 
              onClick={handleSearch} 
              disabled={loading}
              style={{ backgroundColor: '#7c3aed' }}
            >
              <Search className="mr-2" size={18} />
              {loading ? 'Searching...' : 'SEARCH'}
            </Button>
            <Button 
              onClick={handleReset} 
              variant="outline"
            >
              Reset
            </Button>
          </div>
        </div>

        {/* Dispatch Details Section */}
        {latestDispatch && (
          <div className="form-section mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#7c3aed' }}>
              Latest Dispatch Details
            </h2>
            
            <div className="bg-white rounded-lg border-2 p-6 space-y-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between pb-4 border-b">
                <h3 className="text-lg font-semibold">Dispatch Information</h3>
                <span 
                  className={`px-4 py-2 rounded-full font-semibold ${
                    latestDispatch.delivery_status === 'failed' 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-green-100 text-green-700'
                  }`}
                >
                  {latestDispatch.delivery_status === 'failed' ? '❌ FAILED' : '✅ SUCCESS'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Member ID</p>
                  <p className="font-semibold">{latestDispatch.member_id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="font-semibold">{latestDispatch.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Magazine</p>
                  <p className="font-semibold">{latestDispatch.magazine}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Month/Year</p>
                  <p className="font-semibold">{latestDispatch.month} {latestDispatch.year}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Docket No</p>
                  <p className="font-semibold">{latestDispatch.docket_no}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Courier Type</p>
                  <p className="font-semibold">{deliveryLabel(latestDispatch.courier_type)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Dispatch Date</p>
                  <p className="font-semibold">{formatDate(latestDispatch.dispatch_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Dispatched By</p>
                  <p className="font-semibold">{latestDispatch.created_by || 'N/A'}</p>
                </div>
              </div>

              {latestDispatch.delivery_status === 'failed' && (
                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-600">Failure Reason</p>
                  <p className="font-semibold text-red-600">{latestDispatch.failure_reason || 'No reason provided'}</p>
                  <p className="text-sm text-gray-600 mt-2">Failed On</p>
                  <p className="font-semibold">{formatDate(latestDispatch.failure_date)}</p>
                </div>
              )}
            </div>

            {/* Mark Failed Section */}
            {latestDispatch.delivery_status !== 'failed' && (
              <div className="mt-6 bg-red-50 rounded-lg border-2 border-red-200 p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <AlertTriangle className="text-red-600 mt-1" size={24} />
                  <div>
                    <h3 className="text-lg font-bold text-red-700">Mark as Failed Courier</h3>
                    <p className="text-sm text-red-600">This action will mark the dispatch as failed and can be edited later in Dispatch Reports.</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Failure Reason (Optional)</Label>
                    <Textarea
                      value={failureReason}
                      onChange={(e) => setFailureReason(e.target.value)}
                      placeholder="Enter reason for failure (e.g., Address incomplete, Customer refused, etc.)"
                      rows={3}
                    />
                  </div>

                  <Button 
                    onClick={handleMarkFailed}
                    disabled={loading}
                    className="w-full"
                    style={{ backgroundColor: '#dc2626' }}
                  >
                    <AlertTriangle className="mr-2" size={18} />
                    {loading ? 'Marking as Failed...' : 'MARK AS FAILED'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Dispatch History Section */}
        {dispatchHistory.length > 0 && (
          <div className="form-section mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold" style={{ color: '#7c3aed' }}>
                Dispatch History ({dispatchHistory.length})
              </h2>
              <Button 
                variant="outline" 
                onClick={() => setShowHistory(!showHistory)}
              >
                {showHistory ? 'Hide History' : 'Show History'}
              </Button>
            </div>
            
            {showHistory && (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border px-3 py-2 text-left text-sm">Month/Year</th>
                      <th className="border px-3 py-2 text-left text-sm">Dispatch Date</th>
                      <th className="border px-3 py-2 text-left text-sm">Courier</th>
                      <th className="border px-3 py-2 text-left text-sm">Docket No</th>
                      <th className="border px-3 py-2 text-center text-sm">Status</th>
                      <th className="border px-3 py-2 text-left text-sm">Failure Info</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dispatchHistory.map((dispatch, index) => (
                      <tr key={dispatch.id || index} className={dispatch.delivery_status === 'failed' ? 'bg-red-50' : ''}>
                        <td className="border px-3 py-2 text-sm font-medium">
                          {dispatch.month} {dispatch.year}
                        </td>
                        <td className="border px-3 py-2 text-sm">
                          {formatDate(dispatch.dispatch_date)}
                        </td>
                        <td className="border px-3 py-2 text-sm font-bold">
                          {deliveryLabel(dispatch.courier_type)}
                        </td>
                        <td className="border px-3 py-2 text-sm">
                          {dispatch.docket_no}
                        </td>
                        <td className="border px-3 py-2 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            dispatch.delivery_status === 'failed' 
                              ? 'bg-red-100 text-red-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {dispatch.delivery_status === 'failed' ? '❌ Failed' : '✅ Success'}
                          </span>
                        </td>
                        <td className="border px-3 py-2 text-sm">
                          {dispatch.delivery_status === 'failed' ? (
                            <div>
                              <span className="text-red-600">{dispatch.failure_reason || 'No reason'}</span>
                              {dispatch.failure_date && (
                                <div className="text-xs text-gray-500 mt-1">
                                  Failed: {formatDate(dispatch.failure_date)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Summary */}
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-sm text-green-700">
                  <strong>Successful Dispatches:</strong> {dispatchHistory.filter(d => d.delivery_status !== 'failed').length}
                </p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-sm text-red-700">
                  <strong>Failed Dispatches:</strong> {dispatchHistory.filter(d => d.delivery_status === 'failed').length}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarkFailedCourier;
